import React, { lazy, Suspense, useEffect } from 'react'
import { Link, Navigate, Route, Routes, useNavigate } from 'react-router-dom'

import Header from '@layouts/Header'
import Footer from '@layouts/Footer'
import AuthProvider, { useAuth } from './features/auth/AuthProvider'
import AuthModalHost from './features/auth/AuthModalHost'
import ProfilePage from './features/profile/ProfilePage'
import HelpPage from './features/general/pages/HelpPage'
import ContactPage from './features/general/pages/ContactPage'
import PrivacyPolicyPage from './features/general/pages/PrivacyPolicyPage'
import ErrorPage from './features/general/pages/ErrorPage'
import ROUTES from '@shared/lib/constants/routes'
import RemoteGate from './mfe/RemoteGate'

// Sportsbook commented out so dev does not try to load it (sportsbook-web often not running in dev).
// const SportsbookRemote = lazy(() => import('sportsbook/Module'))
const VirtualRemoteLazy = lazy(() => import('virtual/Module'))

function VirtualRemote() {
  const { sessionId, user, openLoginModal } = useAuth()
  return (
    <VirtualRemoteLazy
      platformSessionId={sessionId}
      platformUser={user}
      onAuthRequired={openLoginModal}
    />
  )
}

/** Listens for c7:settings:open and navigates to host profile/settings. Remotes can dispatch this to open settings. */
function SettingsBridge() {
  const navigate = useNavigate()
  useEffect(() => {
    const handler = () => navigate(ROUTES.PLAYER.PROFILE)
    window.addEventListener('c7:settings:open', handler)
    return () => window.removeEventListener('c7:settings:open', handler)
  }, [navigate])
  return null
}

function preferHttpForLocal(url) {
  const s = String(url || '').trim()
  if (!s) return s
  if (s.startsWith('https://') && s.includes('.local')) {
    return `http://${s.slice('https://'.length)}`
  }
  return s
}

function ShellLayout({ children, layout }) {
  const isVirtual = layout === 'virtual'
  const pageClasses = `page animated overflow-x-hidden${isVirtual ? ' virtual-shell-page flex flex-col min-h-0' : ''}`
  const shrinkClass = isVirtual ? 'flex-shrink-0' : ''
  return (
    <div className={pageClasses}>
      <div id="top" className={shrinkClass} />
      {isVirtual ? <div className={shrinkClass}><Header /></div> : <Header />}
      {children}
      {!isVirtual && <Footer />}
    </div>
  )
}

function Lobby() {
  return (
    <ShellLayout>
      <section className="section section-lg bg-gray-100">
        <div className="container">
          <h3 className="text-center mb-5">Lobby</h3>
          <div className="row row-30 justify-content-center">
            <div className="col-12 col-md-6 col-lg-3">
              <Link className="button button-lg button-primary button-block" to="/sports">
                Sportsbook
              </Link>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <Link className="button button-lg button-primary button-block" to="/virtual-sports">
                Virtual Sports
              </Link>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <Link className="button button-lg button-primary button-block" to="/casino">
                Casino
              </Link>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <Link className="button button-lg button-primary button-block" to="/poker">
                Poker
              </Link>
            </div>
          </div>
        </div>
      </section>
    </ShellLayout>
  )
}

/**
 * Full-viewport iframe for casino/poker (phase-1). Renders with no shell header/footer
 * so the iframe fills the entire viewport.
 */
function FullHeightFrame({ src }) {
  if (!src) {
    return (
      <ShellLayout>
        <section className="section section-lg">
          <div className="container">
            <h4>Missing iframe URL</h4>
          </div>
        </section>
      </ShellLayout>
    )
  }

  return (
    <div style={{ width: '100%', height: '100vh', padding: 0, margin: 0 }}>
      <iframe
        src={src}
        frameBorder="0"
        allow="fullscreen"
        style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        title="Casino"
      />
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <AuthModalHost />
      <SettingsBridge />

      <Routes>
        <Route path="/" element={<Lobby />} />

      {/* Sportsbook remote commented out in vite.config and register-remotes; placeholder so /sports does not 404 */}
      <Route
        path="/sports/*"
        element={
          <ShellLayout>
            <section className="section section-lg">
              <div className="container">
                <h4 className="text-center">Sportsbook (temporarily disabled in dev)</h4>
              </div>
            </section>
          </ShellLayout>
        }
      />

      <Route
        path="/virtual-sports/*"
        element={
          <ShellLayout layout="virtual">
            <div className="virtual-route-shell flex flex-1 min-h-0">
              <div className="virtual-sports-host relative z-20 flex flex-1 min-h-0 flex-col overflow-hidden">
                <RemoteGate remote="virtual">
                  <Suspense fallback={<div />}>
                    <div className="virtual-sports-host__viewport relative flex flex-1 min-h-0 flex-col overflow-hidden">
                      <VirtualRemote />
                    </div>
                  </Suspense>
                </RemoteGate>
              </div>
            </div>
          </ShellLayout>
        }
      />

      {/* Casino/Poker iframes commented out for dev; placeholder so routes do not 404 */}
      <Route
        path="/casino/*"
        element={
          <ShellLayout>
            <section className="section section-lg">
              <div className="container">
                <h4 className="text-center">Casino (temporarily disabled in dev)</h4>
              </div>
            </section>
          </ShellLayout>
        }
      />
      <Route
        path="/poker/*"
        element={
          <ShellLayout>
            <section className="section section-lg">
              <div className="container">
                <h4 className="text-center">Poker (temporarily disabled in dev)</h4>
              </div>
            </section>
          </ShellLayout>
        }
      />

        <Route
        path={ROUTES.PLAYER.PROFILE}
        element={
          <ShellLayout>
            <ProfilePage />
          </ShellLayout>
        }
      />

      <Route path={ROUTES.HELP} element={<ShellLayout><HelpPage /></ShellLayout>} />
      <Route path={ROUTES.CONTACT} element={<ShellLayout><ContactPage /></ShellLayout>} />
      <Route path={ROUTES.PRIVACY_POLICY} element={<ShellLayout><PrivacyPolicyPage /></ShellLayout>} />
      <Route path={ROUTES.ERROR} element={<ShellLayout><ErrorPage /></ShellLayout>} />

        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  )
}

export default App
